// ==============================================================
// useNotifications Custom Hook
// Professional hook for notification management with optimistic updates
// ==============================================================

import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead as markAsReadThunk,
  markAllAsRead as markAllAsReadThunk,
  deleteNotification as deleteNotificationThunk,
  clearAllNotifications as clearAllThunk,
  setFilter,
  setTypeFilter,
  addNotification,
  removeNotification,
  optimisticMarkAsRead,
  optimisticMarkAllAsRead,
  optimisticDelete,
  selectNotifications,
  selectUnreadCount,
  selectNotificationsLoading,
  selectNotificationsLoadingMore,
  selectNotificationsError,
  selectNotificationsFilter,
  selectNotificationsTypeFilter,
  selectNotificationsHasMore,
  selectFilteredNotifications,
  type Notification,
  type NotificationType,
} from "@/redux/slices/notificationsSlice";
import { selectIsAuthenticated } from "@/redux/slices/authSlice";
import { useNotificationSound } from "./useNotificationSound";
import { useToast } from "./useToast";

// ==============================================================
// Types
// ==============================================================

interface UseNotificationsOptions {
  autoFetch?: boolean;
  playSound?: boolean;
  pollInterval?: number; // in milliseconds, 0 to disable
}

interface UseNotificationsReturn {
  // Data
  notifications: Notification[];
  filteredNotifications: Notification[];
  unreadCount: number;

  // Loading states
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;

  // Filters
  filter: "all" | "unread" | "read";
  typeFilter: NotificationType | "all";

  // Actions
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;
  setFilter: (filter: "all" | "unread" | "read") => void;
  setTypeFilter: (type: NotificationType | "all") => void;

  // Real-time handlers
  handleNewNotification: (notification: Notification) => void;
  handleNotificationRemoved: (id: string) => void;
}

// ==============================================================
// Hook Implementation
// ==============================================================

export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const {
    autoFetch = true,
    playSound = true,
    pollInterval = 0, // disabled by default
  } = options;

  const dispatch = useAppDispatch();
  const toast = useToast();
  const { play: playNotificationSound } = useNotificationSound();

  // Selectors
  const notifications = useAppSelector(selectNotifications);
  const filteredNotifications = useAppSelector(selectFilteredNotifications);
  const unreadCount = useAppSelector(selectUnreadCount);
  const loading = useAppSelector(selectNotificationsLoading);
  const loadingMore = useAppSelector(selectNotificationsLoadingMore);
  const error = useAppSelector(selectNotificationsError);
  const filter = useAppSelector(selectNotificationsFilter);
  const typeFilter = useAppSelector(selectNotificationsTypeFilter);
  const hasMore = useAppSelector(selectNotificationsHasMore);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Refs for preventing duplicate fetches
  const isFetchingRef = useRef(false);
  const lastUnreadCountRef = useRef(unreadCount);
  const pollingStoppedRef = useRef(false);

  // ==============================================================
  // Actions
  // ==============================================================

  const refresh = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      await Promise.all([
        dispatch(fetchNotifications({ refresh: true, offset: 0 })),
        dispatch(fetchUnreadCount()),
      ]);
    } finally {
      isFetchingRef.current = false;
    }
  }, [dispatch]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || isFetchingRef.current) return;

    await dispatch(
      fetchNotifications({
        offset: notifications.length,
        limit: 20,
      }),
    );
  }, [dispatch, loadingMore, hasMore, notifications.length]);

  const handleMarkAsRead = useCallback(
    async (id: string) => {
      // Optimistic update
      dispatch(optimisticMarkAsRead(id));

      try {
        await dispatch(markAsReadThunk(id)).unwrap();
      } catch (err) {
        // Revert on error - refetch
        dispatch(fetchUnreadCount());
        toast.error("Failed to mark notification as read");
      }
    },
    [dispatch, toast],
  );

  const handleMarkAllAsRead = useCallback(async () => {
    // Optimistic update
    dispatch(optimisticMarkAllAsRead());

    try {
      await dispatch(markAllAsReadThunk()).unwrap();
      toast.success("All notifications marked as read");
    } catch (err) {
      // Revert on error - refetch
      refresh();
      toast.error("Failed to mark all as read");
    }
  }, [dispatch, toast, refresh]);

  const handleDeleteNotification = useCallback(
    async (id: string) => {
      // Optimistic update
      dispatch(optimisticDelete(id));

      try {
        await dispatch(deleteNotificationThunk(id)).unwrap();
      } catch (err) {
        // Revert on error - refetch
        refresh();
        toast.error("Failed to delete notification");
      }
    },
    [dispatch, toast, refresh],
  );

  const handleClearAll = useCallback(async () => {
    try {
      await dispatch(clearAllThunk()).unwrap();
      toast.success("All notifications cleared");
    } catch (err) {
      toast.error("Failed to clear notifications");
    }
  }, [dispatch, toast]);

  const handleSetFilter = useCallback(
    (newFilter: "all" | "unread" | "read") => {
      dispatch(setFilter(newFilter));
    },
    [dispatch],
  );

  const handleSetTypeFilter = useCallback(
    (type: NotificationType | "all") => {
      dispatch(setTypeFilter(type));
    },
    [dispatch],
  );

  // ==============================================================
  // Real-time Handlers
  // ==============================================================

  const handleNewNotification = useCallback(
    (notification: Notification) => {
      dispatch(addNotification(notification));

      // Play sound for new notifications
      if (playSound && !notification.is_read) {
        const soundType = notification.type === "CALL" ? "call" : "notification";
        playNotificationSound(soundType, false, 0.3);
      }

      // Show toast for high priority notifications
      if (notification.priority === "HIGH" || notification.priority === "URGENT") {
        toast.info(notification.title);
      }
    },
    [dispatch, playSound, playNotificationSound, toast],
  );

  const handleNotificationRemoved = useCallback(
    (id: string) => {
      dispatch(removeNotification(id));
    },
    [dispatch],
  );

  // ==============================================================
  // Effects
  // ==============================================================

  // Auto fetch on mount
  useEffect(() => {
    if (autoFetch) {
      refresh();
    }
  }, [autoFetch, refresh]);

  // Polling for new notifications
  useEffect(() => {
    // Reset polling stopped flag when authentication state changes
    pollingStoppedRef.current = false;

    // Don't poll if:
    // - Polling is disabled (pollInterval <= 0)
    // - User is not authenticated
    // - Polling was stopped due to auth errors
    if (pollInterval <= 0 || !isAuthenticated || pollingStoppedRef.current) {
      return;
    }

    const interval = setInterval(async () => {
      // Double-check authentication before making request
      if (pollingStoppedRef.current) {
        clearInterval(interval);
        return;
      }

      try {
        // Use unwrap() to get the actual result or throw on rejection
        await dispatch(fetchUnreadCount()).unwrap();
      } catch (err: any) {
        // Check if it's an authentication error (401/403)
        // Error structure from rejectWithValue: { message, statusCode, response }
        const statusCode =
          err?.statusCode || err?.response?.status || err?.response?.data?.statusCode;

        if (statusCode === 401 || statusCode === 403) {
          // Stop polling on auth errors to prevent spam
          pollingStoppedRef.current = true;
          clearInterval(interval);
          // Don't log or show error - auth errors are handled elsewhere
        }
        // For other errors, continue polling (network issues, etc.)
      }
    }, pollInterval);

    return () => clearInterval(interval);
  }, [pollInterval, dispatch, isAuthenticated]);

  // Play sound when unread count increases
  useEffect(() => {
    if (playSound && unreadCount > lastUnreadCountRef.current) {
      playNotificationSound("notification", false, 0.3);
    }
    lastUnreadCountRef.current = unreadCount;
  }, [unreadCount, playSound, playNotificationSound]);

  // ==============================================================
  // Return
  // ==============================================================

  return {
    // Data
    notifications,
    filteredNotifications,
    unreadCount,

    // Loading states
    loading,
    loadingMore,
    error,
    hasMore,

    // Filters
    filter,
    typeFilter,

    // Actions
    refresh,
    loadMore,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    deleteNotification: handleDeleteNotification,
    clearAll: handleClearAll,
    setFilter: handleSetFilter,
    setTypeFilter: handleSetTypeFilter,

    // Real-time handlers
    handleNewNotification,
    handleNotificationRemoved,
  };
};

export default useNotifications;
